import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import type { EditorActions } from '@atlaskit/editor-core';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	ConfigPanelFeedbackCollector,
	ExtensionsConfigWrapper,
	FeedbackType,
	PremiumGateForExtensionConfigPanel,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import { CUSTOM_SEARCH_EXTENSION_ANALYTIC_SOURCE, SEARCH_EXTENSION_TYPE } from '../../constants';
import { useSearchParametersAndActions } from '../../controllers/use-search-parameters-and-actions';
import type { SearchExtensionParameters } from '../../types';

import { FiltersContent } from './filters-content';
import { messages } from './messages';
import { StylesContent } from './styles-content';

const styles = cssMap({
	title: {
		paddingInline: token('space.100'),
	},
	preamble: {
		paddingInline: token('space.100'),
	},
	stickyFooter: {
		zIndex: 1,
		position: 'sticky',
		bottom: token('space.0'),
		paddingBlock: token('space.050'),
		backgroundColor: token('elevation.surface'),
	},
	footerContentWrapper: {
		paddingBlock: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
});

const SearchConfigPanelFeedbackCollector = () => {
	return (
		<Box xcss={styles.stickyFooter}>
			<Box xcss={styles.footerContentWrapper}>
				<ConfigPanelFeedbackCollector
					source={CUSTOM_SEARCH_EXTENSION_ANALYTIC_SOURCE}
					type={FeedbackType.CUSTOM_SEARCH}
				/>
			</Box>
		</Box>
	);
};

export type SearchConfigPanelContentProps = {
	closeConfigPanel: () => void;
	contentId: string;
	editorActions: EditorActions;
	extensionKey: string;
	localId: string;
};

export const SearchConfigPanelContent = ({
	closeConfigPanel,
	localId,
	editorActions,
}: SearchConfigPanelContentProps): React.JSX.Element => {
	const { extensionActions, extensionParameters } = useSearchParametersAndActions({
		localId,
		editorActions,
		extensionType: SEARCH_EXTENSION_TYPE,
	});
	const { updateMainFormField } = extensionActions;

	const { cloudId, userId, displayName, orgId, isAdminHubAIEnabled, isSSRProductInDarkMode } =
		useProductDependencies();

	const filters = extensionParameters.filters;
	const setFilters = useCallback(
		(newFilters: SearchExtensionParameters['filters']) => {
			updateMainFormField({
				...extensionParameters,
				filters: { ...newFilters, product: 'confluence' as const },
			});
		},
		[extensionParameters, updateMainFormField],
	);

	const extensionStyles = extensionParameters?.styles || {};
	const setStyles = useCallback(
		(newStyles: SearchExtensionParameters['styles']) => {
			updateMainFormField({ ...extensionParameters, styles: newStyles });
		},
		[extensionParameters, updateMainFormField],
	);

	return (
		<ExtensionsConfigWrapper
			title={
				<Box xcss={styles.title}>
					<FormattedMessage {...messages.title} />
				</Box>
			}
			footerComponent={SearchConfigPanelFeedbackCollector}
			closeConfigPanel={closeConfigPanel}
		>
			<Box xcss={styles.preamble}>
				<Text color="color.text.subtle" size="small">
					<FormattedMessage {...messages.preamble} />
				</Text>
			</Box>
			<PremiumGateForExtensionConfigPanel feature="premiumSearchConfig">
				<StylesContent
					styles={extensionStyles}
					setStyles={setStyles}
					isSSRProductInDarkMode={isSSRProductInDarkMode}
				/>
				<FiltersContent
					filters={filters}
					setFilters={setFilters}
					cloudId={cloudId}
					userId={userId ?? null}
					displayName={displayName ?? null}
					orgId={orgId}
					isAdminHubAIEnabled={isAdminHubAIEnabled ?? false}
				/>
			</PremiumGateForExtensionConfigPanel>
		</ExtensionsConfigWrapper>
	);
};
